import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiUsers,
  FiAward,
  FiMap,
  FiSettings,
  FiMenu,
  FiX,
  FiBell,
  FiSearch,
  FiLogOut,
  FiChevronDown,
  FiImage,
  FiBook,
  FiEdit3,
  FiBarChart2,
  FiTerminal,
  FiFileText,
  FiGlobe,
  FiUserCheck,
  FiMessageSquare,
  FiHelpCircle,
  FiSmartphone,
  FiActivity,
  FiCalendar,
  FiGift
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { getRoleDisplayName } from '../config/permissions';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const userMenuRef = useRef(null);

  // Helper function to get full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    // Remove /api suffix for static files
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
    return `${baseUrl}${avatarPath}`;
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Role-based navigation filtering
  const userRole = user?.role || 'editor';

  // Define all navigation items with role restrictions and translation keys
  // Order: Dashboard, Statistics, Map & Challenges, Challenge Gallery, NFT Badges, Users
  const allNavigation = [
    { name: t('cms.nav.dashboard'), href: '/', icon: FiHome, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.statistics'), href: '/statistics', icon: FiBarChart2, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.map'), href: '/map', icon: FiMap, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.gallery'), href: '/challenge-gallery', icon: FiImage, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.events'), href: '/events', icon: FiCalendar, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.rewards'), href: '/rewards', icon: FiGift, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.nfts'), href: '/nfts', icon: FiAward, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.users'), href: '/users', icon: FiUsers, roles: ['master_admin', 'admin', 'editor'] },
  ];

  // Management section items (below App Settings)
  const allManagementItems = [
    { name: t('cms.nav.adminSupport'), href: '/admin-tickets', icon: FiMessageSquare, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.userSupport'), href: '/user-support', icon: FiHelpCircle, roles: ['master_admin', 'admin'] },
    { name: t('cms.nav.adminManagement'), href: '/admin-management', icon: FiUserCheck, roles: ['master_admin'], badge: 'Master' },
    { name: t('cms.nav.terminal'), href: '/terminal', icon: FiTerminal, roles: ['master_admin'], badge: 'Master' },
    { name: t('cms.nav.activityLog'), href: '/activity-log', icon: FiActivity, roles: ['master_admin'], badge: 'Master' },
    { name: t('cms.nav.notes'), href: '/notes', icon: FiEdit3, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.settings'), href: '/settings', icon: FiSettings, roles: ['master_admin', 'admin'] },
  ];

  const allWebsiteItems = [
    { name: t('cms.nav.websiteSettings'), href: '/website-settings', icon: FiGlobe, roles: ['master_admin', 'admin'] },
    { name: t('cms.nav.landingPage'), href: '/landing-page', icon: FiEdit3, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.articles'), href: '/articles', icon: FiFileText, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.imagesGallery'), href: '/gallery', icon: FiImage, roles: ['master_admin', 'admin', 'editor'] },
  ];

  const allAppItems = [
    { name: t('cms.nav.appSettings'), href: '/app-settings', icon: FiSmartphone, roles: ['master_admin'] },
  ];

  const allDocumentationItems = [
    { name: t('cms.nav.documentation'), href: '/documentation', icon: FiBook, roles: ['master_admin', 'admin', 'editor'] },
    { name: t('cms.nav.images'), href: '/images', icon: FiImage, roles: ['master_admin', 'admin', 'editor'] },
  ];

  // Filter items based on user role
  const navigation = allNavigation.filter(item => item.roles.includes(userRole));
  const websiteItems = allWebsiteItems.filter(item => item.roles.includes(userRole));
  const appItems = allAppItems.filter(item => item.roles.includes(userRole));
  const managementItems = allManagementItems.filter(item => item.roles.includes(userRole));
  const documentationItems = allDocumentationItems.filter(item => item.roles.includes(userRole));

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-primary-500 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-primary-600 flex-shrink-0">
          <div className="flex items-center">
            <img
              src="/images/gamimap-logo.png"
              alt="gamimap"
              className="h-8 w-auto brightness-0 invert"
            />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-primary-600 text-white"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <nav className="mt-6 px-3 flex-1 overflow-y-auto pb-20">
          {/* Challenges Section */}
          <div className="px-3">
            <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
              {t('cms.nav.section.challenges')}
            </div>
            <div className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 font-heading
                      ${active
                        ? 'text-secondary-500 font-bold bg-white/35'
                        : 'text-white font-medium hover:bg-primary-600'
                      }
                    `}
                  >
                    <Icon className={`mr-3 w-5 h-5 ${active ? 'text-secondary-500' : 'text-white/80'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Website Section */}
          <div className="mt-8 px-3">
            <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
              {t('cms.nav.section.website')}
            </div>
            <div className="space-y-1">
              {websiteItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 font-heading
                      ${active
                        ? 'text-secondary-500 font-bold bg-white/35'
                        : 'text-white font-medium hover:bg-primary-600'
                      }
                    `}
                  >
                    <Icon className={`mr-3 w-5 h-5 ${active ? 'text-secondary-500' : 'text-white/80'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* App Settings Section */}
          {appItems.length > 0 && (
            <div className="mt-8 px-3">
              <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
                {t('cms.nav.section.app')}
              </div>
              <div className="space-y-1">
                {appItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 font-heading
                        ${active
                          ? 'text-secondary-500 font-bold bg-white/35'
                          : 'text-white font-medium hover:bg-primary-600'
                        }
                      `}
                    >
                      <Icon className={`mr-3 w-5 h-5 ${active ? 'text-secondary-500' : 'text-white/80'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Management Section */}
          {managementItems.length > 0 && (
            <div className="mt-8 px-3">
              <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
                {t('cms.nav.section.management')}
              </div>
              <div className="space-y-1">
                {managementItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-200 font-heading
                        ${active
                          ? 'text-secondary-500 font-bold bg-white/35'
                          : 'text-white font-medium hover:bg-primary-600'
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <Icon className={`mr-3 w-5 h-5 ${active ? 'text-secondary-500' : 'text-white/80'}`} />
                        {item.name}
                      </div>
                      {item.badge && (
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-primary-500 text-white">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Documentation Section */}
          <div className="mt-8 px-3">
            <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2">
              {t('cms.nav.section.documentation')}
            </div>
            <div className="space-y-1">
              {documentationItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center px-3 py-2 rounded-lg text-sm transition-all duration-200 font-heading
                      ${active
                        ? 'text-secondary-500 font-bold bg-white/35'
                        : 'text-white font-medium hover:bg-primary-600'
                      }
                    `}
                  >
                    <Icon className={`mr-3 w-5 h-5 ${active ? 'text-secondary-500' : 'text-white/80'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Footer info */}
        <div className="flex-shrink-0 p-4 border-t border-primary-600 bg-primary-500">
          <div className="flex items-center text-sm text-white/70">
            <div className="flex-1">
              <p className="text-white font-medium">gamimap CMS</p>
              <p>v1.0.3</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100"
            >
              <FiMenu className="w-5 h-5" />
            </button>

            {/* Search bar */}
            <div className="flex-1 max-w-lg mx-auto lg:mx-0">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('cms.header.search')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
            </div>

            {/* Right section */}
            <div className="flex items-center space-x-4">
              <button className="p-2 text-text-light hover:text-primary-500 hover:bg-primary-50 rounded-lg relative transition-colors">
                <FiBell className="w-5 h-5" />
                <span className="absolute top-0 right-0 w-2 h-2 bg-gradient-to-r from-secondary-500 to-danger-500 rounded-full"></span>
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* User dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-secondary-500 to-primary-500 rounded-full flex items-center justify-center shadow-sm overflow-hidden">
                    {user?.avatar ? (
                      <img
                        src={getAvatarUrl(user.avatar)}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-sm font-medium">
                        {user?.username?.charAt(0).toUpperCase() || 'A'}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700 hidden sm:block">
                    {user?.fullName || user?.username || 'Admin User'}
                  </span>
                  <FiChevronDown className="w-4 h-4 text-gray-500" />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.fullName || user?.username}
                      </p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                      <div className="mt-2 flex items-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user?.role === 'master_admin' ? 'bg-primary-100 text-primary-800' :
                          user?.role === 'admin' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {getRoleDisplayName(user?.role)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FiLogOut className="mr-3 w-4 h-4" />
                      {t('cms.header.signOut')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;